// Lobby UI: shown on page load, lists active battles + lets the player
// pick one to join (or create a new battle from a built-in or uploaded
// map). Resolves with the chosen room id + display name; main.ts then
// opens its WebSocket pointed at /ws?room=<id> and forwards the name
// through c2s.hello.
//
// Visual design: see design_handoff_lobby_redesign/ in the original
// handoff bundle. Class-based markup; styles live in index.html under
// the .lobby-overlay scope. Pixel-perfect retro tactical CRT look —
// black background, hard 2px borders, hazard-stripe dividers, phosphor-
// green inputs with subtle glow, no rounded corners anywhere.

import {
  BOT_PERSONALITIES,
  GameType,
  type GameTypeCode,
  MAX_NAME_LENGTH,
  type BotPersonality,
  type CreateRoomResponse,
  type LobbyResponse,
  type PlayerStatsResponse,
} from '@bolo2k/shared';

const GAME_TYPE_LABELS: Record<GameTypeCode, string> = {
  [GameType.Open]: 'Open',
  [GameType.Tournament]: 'Tournament',
  [GameType.StrictTournament]: 'Strict Tournament',
};

const GAME_TYPE_HINTS: Record<GameTypeCode, string> = {
  [GameType.Open]: 'Full loadout on every respawn.',
  [GameType.Tournament]: 'Respawn shells scale with neutral bases; no mines/trees.',
  [GameType.StrictTournament]: 'Bare-tank respawn — drive to a base for everything.',
};

const PERSONALITY_LABELS: Record<BotPersonality, string> = {
  default: 'Default (Balanced)',
  assassin: 'Assassin',
  defensive: 'Defensive',
  pillhunter: 'Pill Hunter',
  basehunter: 'Base Hunter',
};

const NAME_STORAGE_KEY = 'bolo2k.playerName';

// How often the lobby refetches /api/lobby while it's open. Refreshes the
// rooms list (catches battles spawned by other players), the header
// counters, and the per-room callsign-collision advisory. 2 s is a cheap
// poll for a tiny JSON payload and keeps the UI feeling live without
// hammering the server.
const LOBBY_POLL_MS = 2_000;

function loadStoredName(): string {
  try {
    const stored = localStorage.getItem(NAME_STORAGE_KEY) ?? '';
    // Discard names from the pre-CamelCase generator (commit c9182e6) so a
    // returning visitor gets the new format on next load. The old generator
    // emitted "Adjective Noun" (always with a space); the current generator
    // concatenates Rank + Noun as CamelCase. A space in the stored value is
    // a reliable signal of the legacy format.
    if (stored.includes(' ')) {
      localStorage.removeItem(NAME_STORAGE_KEY);
      return '';
    }
    return stored;
  } catch {
    return '';
  }
}

function saveStoredName(name: string): void {
  try {
    if (name) localStorage.setItem(NAME_STORAGE_KEY, name);
    else localStorage.removeItem(NAME_STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode, etc.) — fall through silently.
  }
}

// Military callsign generator — Rank + Noun emitted as CamelCase
// ("CaptainCrunch", "MajorTom", "ColonelMustard"). The CamelCase form gives
// off a retro arcade-handle vibe that suits the lobby's CRT aesthetic.
// 16 × 57 = 912 unique combos so collisions in an
// 8-player room are vanishingly rare. Pure cosmetic — server still keys
// players by owner slot, callsign is just a display label. Earlier
// (pre-2026-05-09) form was Adjective+Noun ("Iron Wolf"); the rank format
// reads more like an actual call sign and pairs well with the lobby's
// retro-tactical aesthetic.
//
// Low enlisted ranks (Private, Corporal) are intentionally dropped — most
// of the iconic "Rank Word" callsign tropes use mid-NCO and officer ranks.
//
// A small dose of pirate flavour is mixed in (~22% chance of any pirate
// element per roll, ~1.4% chance of a fully-pirate combo). Reads as an
// occasional Easter egg ("Corsair Kraken", "Chief Davy") rather than a
// theme overhaul.
const CALLSIGN_RANKS = [
  // Military
  'Sergeant', 'Lieutenant', 'Captain', 'Major', 'Colonel', 'General',
  'Brigadier', 'Marshal', 'Admiral', 'Commodore', 'Commander', 'Chief',
  'Centurion', 'Warrant',
  // Pirate Easter eggs
  'Buccaneer', 'Corsair',
];
const CALLSIGN_NOUNS = [
  // Animals — classic call-sign material
  'Wolf', 'Hawk', 'Eagle', 'Falcon', 'Bear', 'Dragon', 'Phoenix', 'Raven',
  'Cobra', 'Tiger', 'Talon', 'Kestrel',
  // Force / weather
  'Storm', 'Thunder', 'Tempest', 'Inferno', 'Vortex', 'Blaze', 'Frost',
  'Steel', 'Iron',
  // Personality / archetype
  'Maverick', 'Outlaw', 'Renegade', 'Knight', 'Ranger', 'Striker',
  'Spectre', 'Phantom', 'Wraith', 'Reaper', 'Crusader', 'Vanguard',
  'Saber', 'Ghost', 'Sentry', 'Lance',
  // NATO phonetic / military jargon handles
  'Bravo', 'Echo', 'Sierra', 'Tango', 'Apollo', 'Six', 'Actual',
  // Tropes / iconic
  'Crunch',
  // Virtue / abstract
  'Justice', 'Valor', 'Liberty', 'Honor', 'Vengeance', 'Fury',
  // Pirate Easter eggs (named pirates + sea-lore)
  'Kraken', 'Blackbeard', 'Hook', 'Bones', 'Roger', 'Davy',
];

function randomCallsign(): string {
  const r = CALLSIGN_RANKS[Math.floor(Math.random() * CALLSIGN_RANKS.length)];
  const n = CALLSIGN_NOUNS[Math.floor(Math.random() * CALLSIGN_NOUNS.length)];
  // Concatenated as CamelCase ("CaptainCrunch") — gives off a retro
  // arcade-handle / call-sign-as-screen-name feel that suits the lobby's
  // CRT aesthetic better than the spaced form. Each list entry is already
  // capitalized, so the concatenation falls out naturally. Longest combo
  // is "LieutenantBlackbeard" at 20 chars — fits within MAX_NAME_LENGTH.
  return `${r}${n}`;
}

export interface LobbyChoice {
  roomId: string;
  name: string;
  spectate: boolean;
  /** Bots to spawn immediately after the player's own join lands. Each entry
   *  is sent as a c2s.addBot. Empty for spectators (no panel shown). */
  bots: BotPersonality[];
}

export async function showLobby(root: HTMLElement): Promise<LobbyChoice> {
  const lobby = await fetchLobby();

  // Prefer "Everard Island" as default if it's in the map list (it's the
  // canonical first map per BOLO_HISTORY); otherwise just take the first.
  const defaultMap = lobby.maps.find((m) => /everard/i.test(m.name))?.name
    ?? lobby.maps[0]?.name
    ?? '';

  const overlay = document.createElement('div');
  overlay.id = 'lobby-overlay';
  overlay.className = 'lobby-overlay';
  overlay.innerHTML = renderLobbyHTML(lobby, defaultMap);
  root.appendChild(overlay);

  return new Promise<LobbyChoice>((resolve) => {
    bindLobby(overlay, lobby, defaultMap, resolve);
  });
}

// ---------------------------------------------------------------------------
// HTML template — class-based; see index.html for matching styles.
// ---------------------------------------------------------------------------

function renderLobbyHTML(lobby: LobbyResponse, defaultMap: string): string {
  const playersCount = lobby.rooms.reduce((sum, r) => sum + r.players, 0);
  const battlesCount = lobby.rooms.length;
  // Server-persisted counter; padded to 4 digits to match the design's
  // tactical-readout look. Falls through to the natural width past 9999 so
  // the digit grows with the count, never truncates.
  const gamesPlayedDisplay = String(lobby.gamesPlayed).padStart(4, '0');
  const initialName = loadStoredName() || randomCallsign();
  const callsignAttr = escapeAttr(initialName);

  // Pillbox SVG — inline per the design handoff, drawn at 16×16 logical
  // pixels and scaled by CSS to 28×28. Cream centre, dark "eye", red corner
  // spikes. See ui_kits/game/Sprites.jsx in the design project.
  const pillboxSvg = `
    <svg class="pillbox-deco" viewBox="0 0 16 16" shape-rendering="crispEdges" aria-hidden="true">
      <rect x="0" y="0"  width="16" height="16" fill="#1a1a1a"/>
      <rect x="2" y="2"  width="12" height="12" fill="#f0e8b0"/>
      <rect x="6" y="6"  width="4"  height="4"  fill="#1a1a1a"/>
      <rect x="0" y="0"  width="3"  height="3"  fill="#c14a3a"/>
      <rect x="13" y="0" width="3"  height="3"  fill="#c14a3a"/>
      <rect x="0" y="13" width="3"  height="3"  fill="#c14a3a"/>
      <rect x="13" y="13" width="3" height="3"  fill="#c14a3a"/>
    </svg>`;

  const mapOptions = lobby.maps
    .map((m) => `<option value="${escapeAttr(m.name)}"${m.name === defaultMap ? ' selected' : ''}>${escapeHTML(m.name)}</option>`)
    .join('');

  const botOptions = BOT_PERSONALITIES
    .map((p) => `<option value="${escapeAttr(p)}">${escapeHTML(PERSONALITY_LABELS[p])}</option>`)
    .join('');

  const gameTypeOptions = ([GameType.Open, GameType.Tournament, GameType.StrictTournament] as GameTypeCode[])
    .map((g) => `<option value="${g}"${g === GameType.Open ? ' selected' : ''}>${escapeHTML(GAME_TYPE_LABELS[g])}</option>`)
    .join('');

  const roomsList = renderRoomsListHTML(lobby.rooms, initialName);

  return `
    <div class="scanlines" aria-hidden="true"></div>
    <div class="lobby-panel">
      <div class="hazard" aria-hidden="true"></div>

      <div class="head">
        <div class="head-left">
          <div class="brand-row">
            ${pillboxSvg}
            <div class="brand">BOLO<em class="twok">2K</em><span class="hq">HQ</span></div>
          </div>
          <div class="tagline">// pick a battle — or <em>start a new one</em></div>
        </div>
        <div class="readout" aria-live="polite">
          <div class="row"><span class="dim">PLAYERS</span><span><span class="live"></span><span data-players>${playersCount}</span></span></div>
          <div class="row"><span class="dim">BATTLES</span><span data-battles>${battlesCount}</span></div>
          <div class="row"><span class="dim">GAMES PLAYED</span><span data-games-played>${gamesPlayedDisplay}</span></div>
        </div>
      </div>

      <div class="section">
        <h3><span class="marker"></span>Your Callsign</h3>
        <div class="field">
          <span class="prefix">&gt;</span>
          <input type="text" data-callsign value="${callsignAttr}" maxlength="${MAX_NAME_LENGTH}" autocomplete="off" spellcheck="false" />
        </div>
        <p class="callsign-warn-line" data-callsign-warn hidden>// callsign in use — auto-suffix on join</p>
        <p class="help callsign-stats" data-callsign-stats style="margin-top:8px; font-size:13px;">// loading…</p>
      </div>

      <div class="section">
        <h3><span class="marker"></span>Active Battles</h3>
        <p class="help">Drop into an in-progress battle. Spectate to watch without joining the fight.</p>
        <div class="rooms" data-rooms>${roomsList}</div>
      </div>

      <div class="or-divider" aria-hidden="true"><span>or start a new one</span></div>

      <div class="section">
        <h3><span class="marker"></span>Terrain</h3>
        <p class="help">Pick a built-in map and create a new battle.</p>
        <div class="row">
          <div class="field grow">
            <span class="prefix">MAP</span>
            <select data-map-select>${mapOptions}</select>
          </div>
        </div>
        <p class="help submit-map-line" style="margin-top:8px; font-size:14px;">// <a href="#" data-submit-map>submit</a> new map for inclusion</p>

        <h3 style="margin-top:22px;"><span class="marker"></span>Game Mode</h3>
        <div class="row">
          <div class="field grow">
            <span class="prefix">MODE</span>
            <select data-game-type>${gameTypeOptions}</select>
          </div>
          <label class="hidden-mines-toggle">
            <input type="checkbox" data-hidden-mines checked />
            <span>Hidden mines</span>
          </label>
        </div>
        <p class="help" data-game-type-hint style="margin-top:6px; font-size:13px;">${escapeHTML(GAME_TYPE_HINTS[GameType.Open])}</p>

        <h3 style="margin-top:22px;"><span class="marker"></span>Bots <span class="optional">— optional</span></h3>
        <div class="bot-row">
          <div class="field grow">
            <span class="prefix">AI Brain</span>
            <select data-bot-type>${botOptions}</select>
          </div>
          <button class="btn" type="button" data-add-bot>+ Add Bot</button>
        </div>
        <div class="bot-chips" data-bot-chips>
          <span class="bot-empty">// none — pure human game</span>
        </div>

        <div class="create-cta">
          <div class="status-line" data-status></div>
          <button class="btn primary" type="button" data-create-battle>Create Battle</button>
        </div>
      </div>

      <div class="hazard" aria-hidden="true"></div>
    </div>`;
}

// Builds the inner HTML for the rooms list. Extracted so the polling loop
// can re-render it on /api/lobby refresh AND on callsign-input change (the
// per-room "callsign in use" advisory recomputes against the typed name).
// `currentCallsign` is trim+slice-normalised on the caller side; the room's
// own callsigns are already server-sanitised, so a case-sensitive == suffices.
function renderRoomsListHTML(rooms: LobbyResponse['rooms'], currentCallsign: string): string {
  if (rooms.length === 0) {
    return '<div class="room empty">// no active battles — start a new one below</div>';
  }
  const myName = currentCallsign.trim().slice(0, MAX_NAME_LENGTH);
  return rooms.map((r) => {
    const full = r.players >= r.capacity;
    const popClass = full ? 'pop full' : 'pop';
    // Display name = map name unless the room was created with a distinct
    // name (rare); the design list is intentionally simple so we collapse
    // to map name when name === mapName.
    const displayName = r.name === r.mapName ? r.mapName : `${r.name} — ${r.mapName}`;
    // Collision check is against the room's ACTIVE callsigns only — pending-
    // rejoin slots are intentionally NOT exposed here, since typing a pending
    // name in the lobby IS the rejoin claim, not a conflict.
    const collides = myName.length > 0 && r.callsigns.includes(myName);
    const warn = collides
      ? '<div class="callsign-warn">// callsign in use — auto-suffix</div>'
      : '';
    return `
      <div class="room" data-room-id="${escapeAttr(r.id)}">
        <div class="map">${escapeHTML(displayName)}</div>
        ${warn}
        <div class="${popClass}">${r.players} / ${r.capacity}</div>
        <div class="actions">
          <button class="btn primary" type="button" data-join${full ? ' disabled' : ''}>Join</button>
          <button class="btn ghost" type="button" data-spectate>Spectate</button>
        </div>
      </div>`;
  }).join('');
}

// ---------------------------------------------------------------------------
// Behaviour — wires the rendered DOM to data flow + the Promise.
// ---------------------------------------------------------------------------

function bindLobby(
  overlay: HTMLElement,
  lobby: LobbyResponse,
  defaultMap: string,
  resolve: (choice: LobbyChoice) => void,
): void {
  const $ = <T extends Element = HTMLElement>(sel: string): T => {
    const el = overlay.querySelector<T>(sel);
    if (!el) throw new Error(`lobby element not found: ${sel}`);
    return el;
  };

  const callsignInput = $<HTMLInputElement>('[data-callsign]');
  const callsignStatsEl = $<HTMLElement>('[data-callsign-stats]');
  const callsignWarnEl = $<HTMLElement>('[data-callsign-warn]');
  const mapSelect = $<HTMLSelectElement>('[data-map-select]');
  const gameTypeSelect = $<HTMLSelectElement>('[data-game-type]');
  const hiddenMinesInput = $<HTMLInputElement>('[data-hidden-mines]');
  const gameTypeHintEl = $<HTMLElement>('[data-game-type-hint]');
  const botType = $<HTMLSelectElement>('[data-bot-type]');
  const addBotBtn = $<HTMLButtonElement>('[data-add-bot]');
  const botChipsEl = $<HTMLElement>('[data-bot-chips]');
  const createBtn = $<HTMLButtonElement>('[data-create-battle]');
  const statusEl = $<HTMLElement>('[data-status]');
  const roomsEl = $<HTMLElement>('[data-rooms]');
  const submitMapLink = overlay.querySelector<HTMLAnchorElement>('[data-submit-map]');

  // ---- Persistent stats for the current callsign ----------------------
  // Fetches GET /api/stats?callsign=<name> and renders a one-line summary
  // under the input. Debounced so rapid typing doesn't spam the endpoint.
  let statsFetchSeq = 0;
  let statsTimer: ReturnType<typeof setTimeout> | null = null;
  const renderCallsignStats = (label: string): void => {
    callsignStatsEl.textContent = label;
  };
  const formatStats = (s: PlayerStatsResponse): string => {
    if (s.gamesPlayed === 0) return `// new callsign — no battles yet`;
    return `// ${s.kills} kills · ${s.deaths} deaths · ${s.pillsBuilt} pillboxes built · ${s.basesCaptured} bases captured · ${s.gamesPlayed} battle${s.gamesPlayed === 1 ? '' : 's'}`;
  };
  const fetchStatsFor = async (name: string): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) {
      renderCallsignStats('// enter a callsign to see your stats');
      return;
    }
    const seq = ++statsFetchSeq;
    try {
      const res = await fetch(`/api/stats?callsign=${encodeURIComponent(trimmed)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as PlayerStatsResponse;
      // Drop stale responses — only the most recent fetch wins.
      if (seq !== statsFetchSeq) return;
      renderCallsignStats(formatStats(body));
    } catch (err) {
      if (seq !== statsFetchSeq) return;
      renderCallsignStats('// stats unavailable');
      // Swallow — stats are best-effort, not blocking.
      void err;
    }
  };
  const queueStatsFetch = (): void => {
    if (statsTimer) clearTimeout(statsTimer);
    statsTimer = setTimeout(() => {
      void fetchStatsFor(callsignInput.value);
    }, 250);
  };
  callsignInput.addEventListener('input', queueStatsFetch);
  // Initial load — show the persisted callsign's stats immediately.
  void fetchStatsFor(callsignInput.value);

  gameTypeSelect.addEventListener('change', () => {
    const code = Number(gameTypeSelect.value) as GameTypeCode;
    gameTypeHintEl.textContent = GAME_TYPE_HINTS[code] ?? '';
  });

  // "// submit new map for inclusion" link — destination TBD. Swallow the
  // click so the placeholder href="#" doesn't scroll the page to the top.
  // Wire to a real submission flow (mailto / form / GitHub issue) when the
  // channel is decided.
  submitMapLink?.addEventListener('click', (ev) => {
    ev.preventDefault();
  });

  // Bot queue: personalities the player wants spawned right after their own
  // join lands. main.ts batches these as c2s.addBot calls after welcome.
  const botQueue: BotPersonality[] = [];

  // ---- Bot chip add / remove -------------------------------------------
  const renderBotChips = (): void => {
    if (botQueue.length === 0) {
      botChipsEl.innerHTML = '<span class="bot-empty">// none — pure human game</span>';
      return;
    }
    botChipsEl.innerHTML = '';
    botQueue.forEach((p, i) => {
      const chip = document.createElement('span');
      chip.className = 'bot-chip';
      chip.title = 'click to remove';
      chip.innerHTML = `${escapeHTML(PERSONALITY_LABELS[p])} <span class="x">×</span>`;
      chip.addEventListener('click', () => {
        botQueue.splice(i, 1);
        renderBotChips();
      });
      botChipsEl.appendChild(chip);
    });
  };

  addBotBtn.addEventListener('click', () => {
    botQueue.push(botType.value as BotPersonality);
    renderBotChips();
  });

  // ---- /api/lobby polling --------------------------------------------
  // Live-refreshes the rooms list, header counters, and per-room callsign-
  // collision advisory every LOBBY_POLL_MS. Same data source as the initial
  // fetch — the polling loop just replaces the in-memory `latestLobby`,
  // then `refreshLobbyView()` syncs DOM. Side benefit beyond callsign
  // collisions: new battles spawned by other players now appear without
  // the user reloading the page.
  let latestLobby: LobbyResponse = lobby;
  const playersEl = $<HTMLElement>('[data-players]');
  const battlesEl = $<HTMLElement>('[data-battles]');
  const gamesPlayedEl = $<HTMLElement>('[data-games-played]');

  // Toggles the under-input amber advisory based on whether the typed
  // callsign collides with any seated callsign across all active rooms.
  // Same source of truth as the per-room indicator inside renderRoomsListHTML.
  const refreshCallsignWarn = (): void => {
    const myName = callsignInput.value.trim().slice(0, MAX_NAME_LENGTH);
    const collides = myName.length > 0 &&
      latestLobby.rooms.some((r) => r.callsigns.includes(myName));
    callsignWarnEl.hidden = !collides;
  };

  const refreshLobbyView = (): void => {
    const rooms = latestLobby.rooms;
    const playersCount = rooms.reduce((sum, r) => sum + r.players, 0);
    playersEl.textContent = String(playersCount);
    battlesEl.textContent = String(rooms.length);
    gamesPlayedEl.textContent = String(latestLobby.gamesPlayed).padStart(4, '0');
    roomsEl.innerHTML = renderRoomsListHTML(rooms, callsignInput.value);
    refreshCallsignWarn();
  };

  let pollTimer: ReturnType<typeof setInterval> | null = null;
  const startPolling = (): void => {
    pollTimer = setInterval(async () => {
      try {
        latestLobby = await fetchLobby();
        refreshLobbyView();
      } catch {
        // Silent — transient fetch failures shouldn't blank the UI. We keep
        // the last successful state and retry on the next interval.
      }
    }, LOBBY_POLL_MS);
  };
  const stopPolling = (): void => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };
  startPolling();
  // Re-render the rooms list on every callsign keystroke so the inline
  // "callsign in use" advisory tracks what the user is typing live.
  callsignInput.addEventListener('input', () => {
    roomsEl.innerHTML = renderRoomsListHTML(latestLobby.rooms, callsignInput.value);
    refreshCallsignWarn();
  });
  refreshCallsignWarn();

  // ---- Active battles: Spectate + Join wiring -------------------------
  // Event delegation keeps the room list cheap to re-render — the polling
  // loop swaps innerHTML on every refresh but the listener on the parent
  // keeps working.
  const finish = (roomId: string, spectate: boolean): void => {
    const name = currentName();
    if (!spectate) saveStoredName(name);
    stopPolling();
    overlay.remove();
    resolve({ roomId, name, spectate, bots: spectate ? [] : [...botQueue] });
  };

  roomsEl.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const roomEl = target.closest<HTMLElement>('.room[data-room-id]');
    if (!roomEl) return;
    const roomId = roomEl.dataset.roomId;
    if (!roomId) return;
    if (target.matches('[data-spectate]')) {
      finish(roomId, true);
    } else if (target.matches('[data-join]') && !(target as HTMLButtonElement).disabled) {
      finish(roomId, false);
    }
  });

  const currentName = (): string =>
    callsignInput.value.trim().slice(0, MAX_NAME_LENGTH);

  // ---- Create Battle: create a new room and enter --------------------
  const setStatus = (msg: string, level: 'info' | 'error' = 'info'): void => {
    statusEl.textContent = msg;
    statusEl.classList.toggle('error', level === 'error');
  };

  createBtn.addEventListener('click', async () => {
    createBtn.disabled = true;
    addBotBtn.disabled = true;
    try {
      const mapName = mapSelect.value;
      if (!mapName) {
        setStatus('no map selected', 'error');
        createBtn.disabled = false;
        addBotBtn.disabled = false;
        return;
      }
      const gameType = Number(gameTypeSelect.value) as GameTypeCode;
      const hiddenMines = hiddenMinesInput.checked;
      setStatus(`creating battle on "${mapName}"…`);
      const created = await createRoom(mapName, gameType, hiddenMines);
      finish(created.id, false);
    } catch (err) {
      setStatus(`create failed: ${String(err)}`, 'error');
      createBtn.disabled = false;
      addBotBtn.disabled = false;
    }
  });

  // `lobby` is now consumed by the polling loop (seeds `latestLobby`).
  // `defaultMap` is consumed by the render template above.
  void defaultMap;
}

// ---------------------------------------------------------------------------
// Network helpers + escapes.
// ---------------------------------------------------------------------------

async function fetchLobby(): Promise<LobbyResponse> {
  const resp = await fetch('/api/lobby');
  if (!resp.ok) throw new Error(`lobby fetch ${resp.status}`);
  return (await resp.json()) as LobbyResponse;
}

async function createRoom(
  mapName: string,
  gameType: GameTypeCode,
  hiddenMines: boolean,
): Promise<CreateRoomResponse> {
  const resp = await fetch('/api/rooms', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ mapName, gameType, hiddenMines }),
  });
  if (!resp.ok) throw new Error(`create-room ${resp.status}`);
  return (await resp.json()) as CreateRoomResponse;
}

function escapeHTML(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(s: string): string {
  return escapeHTML(s);
}
